import React, {
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { st, classes } from './Thumbnail.st.css';
import { CheckboxChecked } from '@wix/wix-ui-icons-common/system';
import Text from '../Text';
import { withFocusable, InjectedFocusableProps } from '../common/Focusable';
import { dataHooks } from './constants';
import Box from '../Box';
import { ThumbnailProps } from './Thumbnail.types';
import { useIcons } from '../WixDesignSystemIconThemeProvider';
import { useDefaultProps } from '../WixDesignSystemDefaultPropsProvider';

const isString = (a: unknown): a is string => typeof a === 'string';

/**
 * Component for showing thumbnails.
 *
 * It takes full space of parent component, works well together with `<Proportion />`
 * */
const Thumbnail = forwardRef<
  { focus: () => void },
  InjectedFocusableProps<ThumbnailProps>
>((componentProps, ref) => {
  const props = useDefaultProps('Thumbnail', componentProps);
  const {
    dataHook,
    className,
    ariaLabel,
    border = true,
    children,
    title,
    description,
    image,
    size = 'medium',
    selected = false,
    disabled = false,
    hideSelectedIcon,
    backgroundImage,
    onClick,
    onMouseOver,
    width,
    height,
    contentAlignment = 'center',
    ellipsis = false,
    maxLines,
    textPosition = 'inside',
    noPadding = false,
    tooltipProps = {},
    skin = 'primary',
    focusableOnFocus,
    focusableOnBlur,
  } = props;

  const icons = useIcons('Thumbnail', {
    CheckboxChecked,
  });

  const thumbnailRef = useRef<HTMLDivElement>(null);

  const hasChildren = !!children;
  const hasBackground = !!backgroundImage;
  const showBottomTitle = (hasChildren || hasBackground) && title;

  useImperativeHandle(ref, () => ({
    focus: () => {
      thumbnailRef.current?.focus();
    },
  }));

  const renderBackgroundLayout = () => {
    return isString(backgroundImage) ? (
      <div
        className={st(classes.backgroundImage, { disabled })}
        data-hook={dataHooks.thumbnailBackgroundImage}
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
    ) : (
      backgroundImage
    );
  };

  const renderBottomTitle = () => {
    return (
      <Box align="center">
        <Text
          className={st(classes.bottomTitle, { selected, disabled })}
          dataHook={dataHooks.thumbnailBottomTitle}
          size={size}
          tagName="div"
          weight="thin"
          ellipsis
          children={title}
          tooltipProps={tooltipProps}
        />
      </Box>
    );
  };

  const renderThumbnailImage = () => {
    return (
      <div
        className={st(classes.imageContainer, { textPosition })}
        data-hook={dataHooks.thumbnailImage}
      >
        {isString(image) ? (
          <img
            src={image}
            className={st(classes.image, { textPosition })}
            alt=""
          />
        ) : (
          image
        )}
      </div>
    );
  };

  const renderTitleAndDescription = () => {
    return (
      <div className={st(classes.thumbnailTextContent, { textPosition, size })}>
        {title && (
          <Text
            className={st(classes.title, { size, textPosition })}
            dataHook={dataHooks.thumbnailTitle}
            size={size}
            weight="normal"
            children={title}
            skin={disabled ? 'disabled' : undefined}
            ellipsis={ellipsis}
            maxLines={maxLines}
            tooltipProps={tooltipProps}
          />
        )}
        {description && (
          <Text
            className={classes.description}
            dataHook={dataHooks.thumbnailDescription}
            size={size}
            weight="thin"
            secondary
            children={description}
            skin={disabled ? 'disabled' : undefined}
            ellipsis={ellipsis}
            maxLines={maxLines}
            tooltipProps={tooltipProps}
          />
        )}
      </div>
    );
  };

  const renderSelectedIcon = useCallback(
    () => (
      <div
        className={classes.selectedIcon}
        data-hook={dataHooks.thumbnailSelectedIcon}
      >
        <icons.CheckboxChecked height="7.8" width="10" />
      </div>
    ),
    [icons],
  );

  const getThumbnailContent = () => {
    /**
     * Ignores the rest of the properties and just renders children
     */
    if (hasChildren) {
      return (
        <div className={st(classes.customChild, { disabled })}>{children}</div>
      );
    }

    /**
     * @deprecated flow
     */
    if (hasBackground) {
      return renderBackgroundLayout();
    }

    return (
      <div className={st(classes.contentContainer, { textPosition })}>
        {image && renderThumbnailImage()}
        {textPosition === 'inside' && renderTitleAndDescription()}
      </div>
    );
  };

  const renderThumbnail = () => {
    return (
      <div
        ref={thumbnailRef}
        style={{ height }}
        className={st(classes.thumbnail, {
          selected,
          disabled,
          size,
          hasBackground,
          hasChildren,
          textPosition,
          contentAlignment,
          noPadding,
          border,
          skin,
        })}
        data-selected={selected}
        data-disabled={disabled}
        tabIndex={disabled ? undefined : 0}
        data-hook={dataHooks.thumbnailWrapper}
        onFocus={focusableOnFocus}
        onBlur={focusableOnBlur}
      >
        {!hideSelectedIcon && selected && renderSelectedIcon()}
        {getThumbnailContent()}
      </div>
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onClick && ['Enter', ' '].includes(event.key)) {
      onClick(event);
    }
  };

  return (
    <div
      style={{ width }}
      className={st(
        classes.root,
        { disabled, border, selected, textPosition, skin },
        className,
      )}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onClick}
      onMouseOver={disabled ? undefined : onMouseOver}
      onKeyDown={disabled ? undefined : onKeyDown}
      data-hook={dataHook}
    >
      {renderThumbnail()}
      {textPosition === 'outside' && renderTitleAndDescription()}
      {showBottomTitle ? renderBottomTitle() : null}
    </div>
  );
});

Thumbnail.displayName = 'Thumbnail';
export default withFocusable(Thumbnail);
